import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Plus,
  Trash,
  PaperPlaneTilt,
  X,
  Clock,
  CheckCircle,
  Users,
} from "@phosphor-icons/react";
import { formatNumber } from "@/lib/utils-format";
import Modal from "@/components/Modal";
import MediaUpload from "@/components/MediaUpload";

export default function Broadcasts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openWizard, setOpenWizard] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/broadcasts");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const sendNow = async (id) => {
    if (!window.confirm("Отправить рассылку прямо сейчас?")) return;
    await api.post(`/broadcasts/${id}/send-now`);
    setTimeout(load, 1000);
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить рассылку?")) return;
    await api.delete(`/broadcasts/${id}`);
    load();
  };

  return (
    <div data-testid="broadcasts-page">
      <PageHeader
        title="Рассылки"
        subtitle="Создание и история рассылок по сегментам аудитории"
        actions={
          <button
            data-testid="create-broadcast-btn"
            onClick={() => setOpenWizard(true)}
            className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md"
          >
            <Plus size={14} weight="bold" /> Новая рассылка
          </button>
        }
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 text-sm py-12 text-center">
          Пока нет рассылок
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((b) => (
            <div
              key={b.id}
              data-testid={`broadcast-row-${b.id}`}
              className="bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3 flex items-start gap-4 hover:border-zinc-700"
            >
              <StatusIcon status={b.status} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[10px] font-mono uppercase text-zinc-500">
                    #{b.id} · {b.content_type}
                  </div>
                  <StatusBadge status={b.status} />
                  <div className="text-[10px] font-mono text-zinc-500">
                    {b.send_time}
                  </div>
                </div>
                <div className="text-sm text-zinc-300 line-clamp-2">
                  {(b.text || "").replace(/<[^>]+>/g, "") || (
                    <span className="text-zinc-600">—</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500 font-mono">
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" className="text-emerald-500" />
                    {formatNumber(b.sent_cnt || 0)} доставлено
                  </span>
                  {(b.failed_cnt || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 text-red-400">
                      <X size={11} weight="bold" />
                      {formatNumber(b.failed_cnt)} ошибок
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {b.status === "pending" && (
                  <button
                    onClick={() => sendNow(b.id)}
                    data-testid={`send-now-${b.id}`}
                    className="text-xs px-2 py-1.5 rounded-md border border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                  >
                    Отправить сейчас
                  </button>
                )}
                <button
                  onClick={() => remove(b.id)}
                  data-testid={`delete-broadcast-${b.id}`}
                  className="text-xs px-2 py-1.5 rounded-md border border-zinc-800 hover:border-red-500/30 hover:text-red-400"
                >
                  <Trash size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {openWizard && (
        <BroadcastWizard
          onClose={() => setOpenWizard(false)}
          onSaved={() => {
            setOpenWizard(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "sent")
    return (
      <div className="w-8 h-8 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
        <CheckCircle size={14} weight="fill" />
      </div>
    );
  return (
    <div className="w-8 h-8 rounded-full bg-blue-500/15 text-blue-400 flex items-center justify-center">
      <Clock size={14} weight="fill" />
    </div>
  );
}

function StatusBadge({ status }) {
  const labels = { sent: "отправлено", pending: "в очереди" };
  const colors = { sent: "#10b981", pending: "#3b82f6" };
  const c = colors[status] || "#a1a1aa";
  return (
    <span
      className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide rounded border"
      style={{ background: `${c}15`, color: c, borderColor: `${c}40` }}
    >
      {labels[status] || status}
    </span>
  );
}

function BroadcastWizard({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([]);
  const [tags, setTags] = useState([]);
  const [audienceCount, setAudienceCount] = useState(null);
  const [form, setForm] = useState({
    target_logic: { in: [], ex: [], type: "registered", tags_in: [], tags_ex: [] },
    send_time: "now",
    content_type: "text",
    text: "",
    file_id: "",
    buttons: [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/tags").then((r) => setTags(r.data));
  }, []);

  const refreshAudience = async () => {
    const { data } = await api.post(
      "/broadcasts/preview-audience",
      form.target_logic
    );
    setAudienceCount(data.count);
  };

  useEffect(() => {
    if (step === 2) refreshAudience();
    // eslint-disable-next-line
  }, [step]);

  const submit = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.post("/broadcasts", form);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const toggleIn = (eid) => {
    const arr = form.target_logic.in.includes(eid)
      ? form.target_logic.in.filter((i) => i !== eid)
      : [...form.target_logic.in, eid];
    setForm({ ...form, target_logic: { ...form.target_logic, in: arr } });
  };

  const toggleEx = (eid) => {
    const arr = form.target_logic.ex.includes(eid)
      ? form.target_logic.ex.filter((i) => i !== eid)
      : [...form.target_logic.ex, eid];
    setForm({ ...form, target_logic: { ...form.target_logic, ex: arr } });
  };

  const toggleTagIn = (tid) => {
    const arr = form.target_logic.tags_in.includes(tid)
      ? form.target_logic.tags_in.filter((i) => i !== tid)
      : [...form.target_logic.tags_in, tid];
    setForm({ ...form, target_logic: { ...form.target_logic, tags_in: arr } });
  };

  const toggleTagEx = (tid) => {
    const arr = form.target_logic.tags_ex.includes(tid)
      ? form.target_logic.tags_ex.filter((i) => i !== tid)
      : [...form.target_logic.tags_ex, tid];
    setForm({ ...form, target_logic: { ...form.target_logic, tags_ex: arr } });
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={`Шаг ${step} из 4`}
      title={
        step === 1
          ? "Аудитория"
          : step === 2
          ? "Время отправки"
          : step === 3
          ? "Контент сообщения"
          : "Подтверждение"
      }
      testid="broadcast-wizard"
      maxWidth="3xl"
      footer={
        <div className="flex justify-between gap-2">
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="px-3 py-2 text-sm rounded-md border border-zinc-800 disabled:opacity-50"
          >
            Назад
          </button>
          {step < 4 ? (
            <button
              data-testid="wizard-next-btn"
              onClick={() => setStep((s) => s + 1)}
              className="px-4 py-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 text-white font-medium"
            >
              Далее
            </button>
          ) : (
            <button
              data-testid="wizard-confirm-btn"
              disabled={saving || !form.text.trim()}
              onClick={submit}
              className="px-4 py-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-medium inline-flex items-center gap-1.5"
            >
              <PaperPlaneTilt size={14} weight="fill" />
              {saving
                ? "Отправляем…"
                : form.send_time === "now"
                ? "Отправить"
                : "Запланировать"}
            </button>
          )}
        </div>
      }
    >
      {/* Step bar */}
      <div className="flex items-center gap-1 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`flex-1 h-1 rounded-full transition-colors ${
              step >= s ? "bg-blue-500" : "bg-zinc-800"
            }`}
          />
        ))}
      </div>

      {/* Step 1: Audience */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <div className="text-xs text-zinc-400 mb-2">Тип аудитории</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "registered", l: "Зарегистрированные" },
                { v: "incomplete", l: "Не завершившие" },
                { v: "all_users", l: "Все пользователи" },
              ].map((a) => (
                <button
                  key={a.v}
                  data-testid={`audience-${a.v}`}
                  onClick={() =>
                    setForm({
                      ...form,
                      target_logic: { ...form.target_logic, type: a.v },
                    })
                  }
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    form.target_logic.type === a.v
                      ? "border-blue-500 bg-blue-500/10 text-blue-400"
                      : "border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  {a.l}
                </button>
              ))}
            </div>
          </div>

          {form.target_logic.type !== "all_users" && (
            <>
              <div>
                <div className="text-xs text-zinc-400 mb-2">
                  Включить только участников ивентов (если пусто — все):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {events.map((ev) => (
                    <button
                      key={ev.id}
                      data-testid={`include-event-${ev.id}`}
                      onClick={() => toggleIn(ev.id)}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        form.target_logic.in.includes(ev.id)
                          ? "border-blue-500 bg-blue-500/10 text-blue-400"
                          : "border-zinc-800 hover:border-zinc-700"
                      }`}
                    >
                      {ev.btn_name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-2">
                  Исключить участников ивентов:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {events.map((ev) => (
                    <button
                      key={ev.id}
                      data-testid={`exclude-event-${ev.id}`}
                      onClick={() => toggleEx(ev.id)}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        form.target_logic.ex.includes(ev.id)
                          ? "border-red-500 bg-red-500/10 text-red-400"
                          : "border-zinc-800 hover:border-zinc-700"
                      }`}
                    >
                      {ev.btn_name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {tags.length > 0 && (
            <>
              <div className="border-t border-zinc-800/60 pt-3">
                <div className="text-xs text-zinc-400 mb-2">
                  Включить только пользователей с метками (если пусто — без фильтра):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      data-testid={`include-tag-${tag.id}`}
                      onClick={() => toggleTagIn(tag.id)}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        form.target_logic.tags_in.includes(tag.id)
                          ? "border-blue-500 bg-blue-500/10 text-blue-400"
                          : "border-zinc-800 hover:border-zinc-700"
                      }`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-2">
                  Исключить пользователей с метками:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      data-testid={`exclude-tag-${tag.id}`}
                      onClick={() => toggleTagEx(tag.id)}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        form.target_logic.tags_ex.includes(tag.id)
                          ? "border-red-500 bg-red-500/10 text-red-400"
                          : "border-zinc-800 hover:border-zinc-700"
                      }`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2: Time */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-4 flex items-center gap-3">
            <Users size={24} className="text-blue-500" />
            <div>
              <div className="text-xs text-zinc-500">Получат сообщение</div>
              <div className="text-2xl font-mono font-semibold">
                {audienceCount === null ? "…" : formatNumber(audienceCount)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="send-time-now"
              onClick={() => setForm({ ...form, send_time: "now" })}
              className={`px-3 py-3 text-sm rounded-md border ${
                form.send_time === "now"
                  ? "border-blue-500 bg-blue-500/10 text-blue-400"
                  : "border-zinc-800"
              }`}
            >
              🚀 Сейчас
            </button>
            <button
              data-testid="send-time-later"
              onClick={() =>
                setForm({
                  ...form,
                  send_time:
                    form.send_time === "now" ? "01.02.2026 12:00" : form.send_time,
                })
              }
              className={`px-3 py-3 text-sm rounded-md border ${
                form.send_time !== "now"
                  ? "border-blue-500 bg-blue-500/10 text-blue-400"
                  : "border-zinc-800"
              }`}
            >
              📅 Запланировать
            </button>
          </div>
          {form.send_time !== "now" && (
            <input
              data-testid="send-time-input"
              value={form.send_time}
              onChange={(e) => setForm({ ...form, send_time: e.target.value })}
              placeholder="01.02.2026 12:00"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          )}
        </div>
      )}

      {/* Step 3: Content */}
      {step === 3 && (
        <div className="space-y-3">
          <Field label="Фото или видео (необязательно)">
            <MediaUpload
              testid="broadcast-media-upload"
              accept="any"
              value={form.file_id && /^(\/api\/|https?:\/\/)/.test(form.file_id) ? form.file_id : ""}
              onChange={(url, kind) =>
                setForm({
                  ...form,
                  file_id: url,
                  content_type: kind || (url ? "photo" : "text"),
                })
              }
              helpText="PNG / JPG до 8 MB или MP4 / MOV до 20 MB. Если без файла — отправится обычное текстовое сообщение."
            />
          </Field>
          <Field label="Текст сообщения (HTML)">
            <textarea
              data-testid="broadcast-text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={6}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
              placeholder="Привет! ..."
            />
          </Field>
          <Field label="Кнопки (Текст — URL)">
            <div className="space-y-1.5">
              {form.buttons.map((b, i) => (
                <div key={i} className="flex gap-1.5">
                  <input
                    value={b.text}
                    onChange={(e) => {
                      const arr = [...form.buttons];
                      arr[i] = { ...arr[i], text: e.target.value };
                      setForm({ ...form, buttons: arr });
                    }}
                    placeholder="Текст"
                    className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
                  />
                  <input
                    value={b.url || ""}
                    onChange={(e) => {
                      const arr = [...form.buttons];
                      arr[i] = { ...arr[i], url: e.target.value };
                      setForm({ ...form, buttons: arr });
                    }}
                    placeholder="https://"
                    className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
                  />
                  <button
                    onClick={() =>
                      setForm({
                        ...form,
                        buttons: form.buttons.filter((_, j) => j !== i),
                      })
                    }
                    className="text-zinc-500 hover:text-red-400 p-1.5"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={() =>
                  setForm({
                    ...form,
                    buttons: [...form.buttons, { text: "", url: "" }],
                  })
                }
                className="text-xs text-zinc-400 hover:text-blue-400 inline-flex items-center gap-1"
              >
                <Plus size={12} /> Добавить кнопку
              </button>
            </div>
          </Field>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <div className="space-y-3">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Summary label="Аудитория" value={form.target_logic.type} />
              <Summary
                label="Получат"
                value={
                  audienceCount === null
                    ? "—"
                    : formatNumber(audienceCount) + " чел."
                }
              />
              <Summary
                label="Включено ивентов"
                value={form.target_logic.in.length || "все"}
              />
              <Summary
                label="Исключено ивентов"
                value={form.target_logic.ex.length || "нет"}
              />
              <Summary
                label="Меток включено"
                value={form.target_logic.tags_in.length || "без фильтра"}
              />
              <Summary
                label="Меток исключено"
                value={form.target_logic.tags_ex.length || "нет"}
              />
              <Summary
                label="Время"
                value={form.send_time === "now" ? "сейчас" : form.send_time}
              />
              <Summary label="Тип" value={form.content_type} />
            </div>
            <div className="mt-4 border-t border-zinc-800 pt-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                Сообщение
              </div>
              <div className="text-sm text-zinc-300 whitespace-pre-wrap break-words">
                {form.text}
              </div>
            </div>
            {form.buttons.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {form.buttons.map((b, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 text-xs rounded bg-[#2AABEE]/10 text-[#2AABEE] border border-[#2AABEE]/30"
                  >
                    {b.text}
                  </span>
                ))}
              </div>
            )}
          </div>
          {err && (
            <div className="text-sm text-red-400" data-testid="broadcast-error">
              {err}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className="text-sm font-mono">{value}</div>
    </div>
  );
}
